/* ============================================================
   Fast-entry parsing — "Buy milk #Groceries @errand" → a name, a project and a
   set of tags. Pure and framework-free, like the rest of shared/: the capture sheet
   drives the live suggestion menu with it, and it is unit-tested on its own.

   The prefixes are Todoist's, which is the largest quick-add vocabulary in the
   category:

     #  project   one per task (the container axis — tasks.project_id)
     @  tag       zero to many (the facet axis — tasks.tag_ids)
     +  assignee  RESERVED, not parsed yet. Todoist spends `+` on people for
                  exactly this reason, and `tasks.assignee_id` already exists
                  (dormant) — so keeping it free now means the collaboration
                  pass needs no vocabulary change. See docs/tags.md.

   Two asymmetries are deliberate, and both are about what a stray token costs:

   - A `#` token only ever SELECTS a project, never creates one. A project owns
     an accent hue and a row in the sidebar, so conjuring one from a typo
     pollutes navigation; an unresolved `#foo` is therefore left in the task's
     name as ordinary text, exactly as typed. Creating a project from here is a
     deliberate tap on the suggestion menu's "New project" row.
     The same rule covers a token that resolves but LOSES: a task has one
     project, so with several typed the last wins and the earlier ones go back
     into the name as text. Cutting them would delete words that changed
     nothing — and "didn't take effect" is one rule, whether the reason was a
     typo or an override.
   - An `@` token DOES create its tag on save (`newTagNames`). A tag is a bare
     label — no colour, no lifecycle, nothing to clean up but the word itself —
     so the cost of a stray one is a line in a picker, against the cost of
     silently dropping what the user typed.

   Tokens must start at a word boundary, which is what keeps "bob@example.com"
   and "issue #42 is back" from being read as markup.
   ============================================================ */

/** Prefix for the container axis — one project per task. */
export const PROJECT_PREFIX = '#';
/** Prefix for the facet axis — zero to many tags per task. */
export const TAG_PREFIX = '@';
/** Prefix for the assignee. RESERVED — not parsed; see the header note. */
export const ASSIGNEE_PREFIX = '+';

/** Which axis a token addresses. */
export type TokenKind = 'project' | 'tag';

/** A prefix token found in the text, as a half-open span [start, end). */
export interface CaptureToken {
  kind: TokenKind;
  /** what was typed after the prefix ('' right after the prefix itself) */
  query: string;
  /** index of the prefix character */
  start: number;
  /** index just past the token */
  end: number;
}

/** The named things a parse resolves against. Ids are opaque; only `name`
    matters to the matching. */
export interface CaptureContext {
  projects: readonly { id: string; name: string }[];
  tags: readonly { id: string; name: string }[];
}

/** A token that RESOLVED, and what it resolved to. The span is the one in the
    original text, which is what lets a caller highlight it in place and cut it
    back out again by hand. */
export interface CaptureCut extends CaptureToken {
  /** the row it resolved to, or null for an `@tag` that doesn't exist yet */
  id: string | null;
}

export interface CaptureParse {
  /** the text with every RESOLVED token removed and whitespace tidied */
  name: string;
  /** the project the text filed into, or null. Last `#` token wins — a task has
      at most one project, and the last thing typed is the live intent. */
  projectId: string | null;
  /** tags the text resolved to existing rows, in order of appearance, deduped */
  tagIds: string[];
  /** `@` tokens matching no existing tag — created on save, in typed order */
  newTagNames: string[];
  /** every resolved token, with its span in the ORIGINAL text. The name above is
      this text minus these spans; a UI that wants to show the tokens where they
      were typed — or let the user take one back — reads them from here. */
  cuts: CaptureCut[];
}

/** Lowercase + strip diacritics, WITHOUT trimming — the length of the result
    has to line up with the input, since `matchProjectName` measures a match in
    characters and then slices the original text by it. */
function fold(s: string): string {
  const lower = s.toLowerCase();
  try {
    // NFD splits an accented letter into base + combining mark; dropping the
    // marks is what makes "Café" and "cafe" the same name.
    return lower.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  } catch {
    return lower;
  }
}

/** Lowercase + strip diacritics + trim — the form two names are compared in.
    Mirrors `fold` in app/src/search.ts: matching a name should never turn on an
    accent or a capital. */
export function foldName(s: string): string {
  return fold(s.trim());
}

/** Trailing sentence punctuation, stripped off a token so "call mom @errand."
    tags `errand` rather than `errand.`. */
const TRAILING_PUNCT = /[.,;:!?)\]]+$/;

/** Is the character before `i` a token boundary? Only start-of-text or
    whitespace, which is what stops an email address and "issue #42"'s inner
    characters from opening a token. */
function atBoundary(text: string, i: number): boolean {
  return i === 0 || /\s/.test(text[i - 1]!);
}

/**
 * The project whose name the text at `from` starts with, preferring the LONGEST
 * — so with both "House" and "House Move" defined, "#House Move" picks the
 * latter.
 *
 * Project names hold spaces ("House Move", "Lisbon Trip"), so a `#` token cannot
 * simply run to the next space the way a tag token can. Matching against the
 * names we already know is the only way to read a multi-word project without
 * quoting; the fallback (stop at whitespace) then handles everything else.
 */
function matchProjectName(
  text: string,
  from: number,
  projects: CaptureContext['projects'],
): { id: string; length: number } | null {
  const rest = fold(text.slice(from));
  let best: { id: string; length: number } | null = null;
  for (const project of projects) {
    const name = project.name.trim();
    if (!name || !rest.startsWith(fold(name))) continue;
    // the match has to end at a boundary, or "#Home" would match a "Ho" project
    const after = text[from + name.length];
    if (after != null && !/\s/.test(after)) continue;
    if (!best || name.length > best.length) best = { id: project.id, length: name.length };
  }
  return best;
}

/** Where a token starting at `start` ends, given no project name matched: the
    run of non-whitespace after the prefix, minus trailing punctuation. */
function tokenEnd(text: string, start: number): number {
  let i = start + 1;
  while (i < text.length && !/\s/.test(text[i]!)) i += 1;
  const raw = text.slice(start + 1, i);
  return start + 1 + raw.replace(TRAILING_PUNCT, '').length;
}

/** Every token in the text, in order. `end` accounts for a multi-word project
    name when one matched (so the span covers "#House Move"). */
function scanTokens(text: string, ctx: CaptureContext): CaptureToken[] {
  const found: CaptureToken[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]!;
    if ((ch !== PROJECT_PREFIX && ch !== TAG_PREFIX) || !atBoundary(text, i)) continue;
    const kind: TokenKind = ch === PROJECT_PREFIX ? 'project' : 'tag';
    const projectMatch =
      kind === 'project' ? matchProjectName(text, i + 1, ctx.projects) : null;
    const end = projectMatch ? i + 1 + projectMatch.length : tokenEnd(text, i);
    found.push({ kind, query: text.slice(i + 1, end), start: i, end });
    i = end - 1;
  }
  return found;
}

/**
 * The token the caret sits in, or null — what the live suggestion menu renders
 * against. `caret` defaults to the end of the text, which is where it is while
 * someone is typing.
 *
 * A token counts as active while the caret is anywhere from just after the
 * prefix to the end of the token, so the menu is open from the moment the
 * prefix is typed (with an empty query, listing everything) and stays open as
 * the query narrows.
 */
export function activeToken(
  text: string,
  ctx: CaptureContext,
  caret: number = text.length,
): CaptureToken | null {
  for (const token of scanTokens(text, ctx)) {
    if (caret > token.start && caret <= token.end) return token;
  }
  return null;
}

/** Cut a token out of the text, tidying the space it leaves behind. Returns the
    new text and where the caret should land — what the sheet does when a
    suggestion is picked, since the chip now carries what the words did. */
export function spliceToken(text: string, token: CaptureToken): { text: string; caret: number } {
  const before = text.slice(0, token.start);
  const after = text.slice(token.end);
  // collapse the double space a removal in the middle of a sentence leaves
  const joined = before + (before.endsWith(' ') && after.startsWith(' ') ? after.slice(1) : after);
  return { text: joined.replace(/\s+$/, ''), caret: before.replace(/\s+$/, '').length };
}

/** Whitespace tidy for a name a token was cut out of. */
function tidy(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

/**
 * Resolve every token in the text — what save time calls.
 *
 * Unresolved tokens follow the asymmetry in the header: a `#` that names no
 * project stays in the name verbatim, while an `@` that names no tag is lifted
 * out into `newTagNames` for the caller to create.
 */
export function parseCapture(text: string, ctx: CaptureContext): CaptureParse {
  const byName = new Map(ctx.tags.map((t) => [foldName(t.name), t.id]));
  let projectId: string | null = null;
  const tagIds: string[] = [];
  const newTagNames: string[] = [];
  // spans to drop from the name, collected then cut back-to-front so earlier
  // indices stay valid
  const cuts: CaptureCut[] = [];

  for (const token of scanTokens(text, ctx)) {
    if (!token.query) continue; // a bare "#" or "@" is just a character
    if (token.kind === 'project') {
      const match = matchProjectName(text, token.start + 1, ctx.projects);
      if (!match) continue; // unresolved -> left in the name as typed
      /* Last `#` wins — a task has one project and the last thing typed is the
         live intent — and the LOSER is put back. Consuming it would delete words
         that changed nothing: "Buy #Groceries milk #House Move" filed under
         House Move while quietly eating "#Groceries" too. A token that doesn't
         win is treated exactly like one that matches nothing (see the header):
         left in the name as ordinary text, where it is visible and deletable. */
      const previous = cuts.findIndex((c) => c.kind === 'project');
      if (previous !== -1) cuts.splice(previous, 1);
      projectId = match.id;
      cuts.push({ ...token, id: match.id });
      continue;
    }
    const id = byName.get(foldName(token.query));
    if (id) {
      if (!tagIds.includes(id)) tagIds.push(id);
    } else {
      const folded = foldName(token.query);
      if (!newTagNames.some((n) => foldName(n) === folded)) newTagNames.push(token.query);
    }
    cuts.push({ ...token, id: id ?? null });
  }

  let name = text;
  // back-to-front, so an earlier cut's indices stay valid
  for (const token of [...cuts].reverse()) {
    name = name.slice(0, token.start) + name.slice(token.end);
  }
  return { name: tidy(name), projectId, tagIds, newTagNames, cuts };
}
